import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../../../services/api.js';

// UC 34268 — View System Activity Log
// Audience: Super Admin / Finance (read-only audit view)

function formatTs(ts) {
  if (!ts) return '—';
  const d = new Date(ts);
  return d.toLocaleString('en-PH', { dateStyle: 'short', timeStyle: 'short' });
}

function moduleBadge(mod) {
  const map = {
    PullOrders: 'badge-primary',
    Transfers: 'badge-info',
    Inventory: 'badge-warning',
    Production: 'badge-success',
    Reorder: 'badge-secondary',
  };
  return `badge ${map[mod] || 'badge-light'}`;
}

export default function SystemActivityLogPage() {
  const today = new Date().toISOString().slice(0, 10);
  const weekAgo = new Date(Date.now() - 7 * 24 * 3600 * 1000).toISOString().slice(0, 10);

  const [dateStart, setDateStart] = useState(weekAgo);
  const [dateEnd, setDateEnd] = useState(today);
  const [moduleFilter, setModuleFilter] = useState('');
  const [actionFilter, setActionFilter] = useState('');
  const [search, setSearch] = useState('');

  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedEntry, setSelectedEntry] = useState(null);

  const [allModules, setAllModules] = useState([]);
  const [allActions, setAllActions] = useState([]);

  // Refs so fetchLogs always reads the latest filter values even when called
  // from an onClick handler that may have captured a stale closure.
  const filterRef = useRef({ dateStart, dateEnd, moduleFilter, actionFilter, search });
  useEffect(() => { filterRef.current = { dateStart, dateEnd, moduleFilter, actionFilter, search }; });

  // Load filter options
  useEffect(() => {
    api.get('/activity-log/meta/modules').then((r) => setAllModules(r.data.data || [])).catch(() => {});
    api.get('/activity-log/meta/actions').then((r) => setAllActions(r.data.data || [])).catch(() => {});
  }, []);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { dateStart: ds, dateEnd: de, moduleFilter: mf, actionFilter: af, search: sr } = filterRef.current;
      const params = new URLSearchParams();
      if (ds) params.set('date_start', ds);
      if (de) params.set('date_end', de);
      if (mf) params.set('module', mf);
      if (af) params.set('action', af);
      if (sr) params.set('search', sr);
      params.set('limit', '100');
      const res = await api.get(`/activity-log?${params}`);
      setLogs(res.data.data || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  }, []); // stable — reads from ref

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  function clearFilters() {
    setDateStart(weekAgo);
    setDateEnd(today);
    setModuleFilter('');
    setActionFilter('');
    setSearch('');
  }

  return (
    <div className="container-fluid py-4" data-testid="activity-log-page">
      <div className="d-flex align-items-center justify-content-between mb-4">
        <div>
          <h2 className="h4 font-weight-bold mb-0">System Activity Log</h2>
          <small className="text-muted">Read-only cross-module audit trail</small>
        </div>
        <span className="badge badge-secondary px-3 py-2" data-testid="log-total">
          {total} entries
        </span>
      </div>

      {/* Filters */}
      <div className="card shadow-sm mb-4" data-testid="filter-panel">
        <div className="card-body">
          <div className="row g-2 align-items-end">
            <div className="col-md-2">
              <label className="form-label small font-weight-bold">Date From</label>
              <input
                type="date"
                className="form-control form-control-sm"
                value={dateStart}
                onChange={(e) => setDateStart(e.target.value)}
                data-testid="filter-date-start"
              />
            </div>
            <div className="col-md-2">
              <label className="form-label small font-weight-bold">Date To</label>
              <input
                type="date"
                className="form-control form-control-sm"
                value={dateEnd}
                onChange={(e) => setDateEnd(e.target.value)}
                data-testid="filter-date-end"
              />
            </div>
            <div className="col-md-2">
              <label className="form-label small font-weight-bold">Module</label>
              <select
                className="form-control form-control-sm"
                value={moduleFilter}
                onChange={(e) => setModuleFilter(e.target.value)}
                data-testid="filter-module"
              >
                <option value="">All Modules</option>
                {allModules.map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
            <div className="col-md-2">
              <label className="form-label small font-weight-bold">Action Type</label>
              <select
                className="form-control form-control-sm"
                value={actionFilter}
                onChange={(e) => setActionFilter(e.target.value)}
                data-testid="filter-action"
              >
                <option value="">All Actions</option>
                {allActions.map((a) => <option key={a} value={a}>{a}</option>)}
              </select>
            </div>
            <div className="col-md-2">
              <label className="form-label small font-weight-bold">Search</label>
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="Reference ID or keyword..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                data-testid="filter-search"
              />
            </div>
            <div className="col-md-2 d-flex flex-column">
              <button
                type="button"
                className="btn btn-primary btn-sm mb-1"
                onClick={fetchLogs}
                disabled={loading}
                data-testid="btn-apply-filter"
              >
                {loading ? 'Loading...' : 'Apply'}
              </button>
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                onClick={clearFilters}
                data-testid="btn-clear-filter"
              >
                Clear
              </button>
            </div>
          </div>
        </div>
      </div>

      {error && <div className="alert alert-danger" data-testid="log-error">{error}</div>}

      {loading && (
        <div className="text-center py-5" data-testid="log-loading">
          <div className="spinner-border text-primary" role="status" />
        </div>
      )}

      {!loading && logs.length === 0 && (
        <div className="alert alert-info text-center" data-testid="log-empty">
          No activity log entries found for the selected filters.
        </div>
      )}

      {!loading && logs.length > 0 && (
        <div className="card shadow-sm">
          <div className="card-body p-0">
            <div className="table-responsive">
              <table className="table table-hover table-sm mb-0" data-testid="activity-log-table">
                <thead className="thead-light">
                  <tr>
                    <th style={{ width: 160 }}>Timestamp</th>
                    <th>Actor</th>
                    <th>Module</th>
                    <th>Entity</th>
                    <th>Reference</th>
                    <th>Action</th>
                    <th>Summary</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((entry) => (
                    <tr key={entry.id} data-testid={`log-row-${entry.id}`}>
                      <td className="text-nowrap small text-muted" data-testid={`log-ts-${entry.id}`}>
                        {formatTs(entry.created_at)}
                      </td>
                      <td data-testid={`log-actor-${entry.id}`}>{entry.actor_name}</td>
                      <td>
                        <span className={moduleBadge(entry.module)} data-testid={`log-module-${entry.id}`}>
                          {entry.module}
                        </span>
                      </td>
                      <td className="text-muted small" data-testid={`log-entity-${entry.id}`}>
                        {entry.entity}
                      </td>
                      <td className="text-nowrap" data-testid={`log-ref-${entry.id}`}>
                        {entry.entity_ref || '—'}
                      </td>
                      <td>
                        <span className="badge badge-secondary" data-testid={`log-action-${entry.id}`}>
                          {entry.action}
                        </span>
                      </td>
                      <td className="text-truncate" style={{ maxWidth: 240 }} data-testid={`log-summary-${entry.id}`}>
                        {entry.summary || '—'}
                      </td>
                      <td>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-secondary"
                          onClick={() => setSelectedEntry(entry)}
                          data-testid={`btn-view-${entry.id}`}
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Detail modal */}
      {selectedEntry && (
        <div
          className="modal fade show d-block"
          style={{ background: 'rgba(0,0,0,0.5)' }}
          data-testid="log-detail-modal"
        >
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header bg-dark text-white">
                <h5 className="modal-title">Activity Entry Detail</h5>
                <button
                  type="button"
                  className="close text-white"
                  onClick={() => setSelectedEntry(null)}
                  data-testid="modal-close"
                >
                  <span>&times;</span>
                </button>
              </div>
              <div className="modal-body" data-testid="log-detail-body">
                <table className="table table-sm table-bordered">
                  <tbody>
                    <tr>
                      <th style={{ width: 160 }}>Timestamp</th>
                      <td data-testid="detail-ts">{formatTs(selectedEntry.created_at)}</td>
                    </tr>
                    <tr>
                      <th>Actor</th>
                      <td data-testid="detail-actor">{selectedEntry.actor_name} ({selectedEntry.actor_email})</td>
                    </tr>
                    <tr>
                      <th>Module</th>
                      <td data-testid="detail-module">{selectedEntry.module}</td>
                    </tr>
                    <tr>
                      <th>Entity</th>
                      <td data-testid="detail-entity">{selectedEntry.entity}</td>
                    </tr>
                    <tr>
                      <th>Reference</th>
                      <td data-testid="detail-ref">{selectedEntry.entity_ref || '—'}</td>
                    </tr>
                    <tr>
                      <th>Entity ID</th>
                      <td data-testid="detail-entity-id" className="small text-muted">{selectedEntry.entity_id || '—'}</td>
                    </tr>
                    <tr>
                      <th>Action</th>
                      <td data-testid="detail-action">{selectedEntry.action}</td>
                    </tr>
                    <tr>
                      <th>Summary</th>
                      <td data-testid="detail-summary">{selectedEntry.summary || '—'}</td>
                    </tr>
                  </tbody>
                </table>
                {/* No edit or delete capability — read-only view */}
                <div className="alert alert-light border mt-2 mb-0 small text-muted" data-testid="readonly-notice">
                  Activity log entries are read-only and cannot be modified or deleted.
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setSelectedEntry(null)}
                  data-testid="modal-close-footer"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
